import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { TRUST_BADGES } from "../lib/assets";
import {
 Phone, Calendar, Star, ShieldCheck, Wrench, Award, MapPin, ChevronRight,
 ArrowRight, CheckCircle2, Clock, DollarSign, Users,
} from "lucide-react";

export const PHONE = "(951) 653-4777";
export const PHONE_RAW = "+19516534777";
export const GOOGLE_REVIEW_URL = "https://g.page/r/CVXf_MPFINPxEAE/review";

/* ---------- Layout primitives ---------- */
export const Container = ({ className = "", children }) => (
 <div className={`mx-auto w-full max-w-7xl px-5 sm:px-8 ${className}`}>{children}</div>
);

export const Section = ({ className = "", children, ...rest }) => (
 <section className={`py-16 sm:py-24 ${className}`} {...rest}>{children}</section>
);

export const Eyebrow = ({ children }) => (
 <span className="inline-flex items-center gap-2 text-primary font-semibold tracking-[0.2em] text-xs">
 <span className="h-px w-8 bg-primary" /> {children}
 </span>
);

export const SectionHeading = ({ eyebrow, title, subtitle, center, light }) => (
 <div className={`${center ? "mx-auto max-w-2xl text-center" : "max-w-3xl"} mb-12`}>
 {eyebrow && <div className="mb-4"><Eyebrow>{eyebrow}</Eyebrow></div>}
 <h2 className={`font-display text-3xl sm:text-4xl lg:text-5xl font-600 leading-[1.05] ${light ? "text-white" : "text-white"}`}>{title}</h2>
 {subtitle && <p className="mt-4 text-muted-foreground text-base sm:text-lg leading-relaxed">{subtitle}</p>}
 </div>
);

/* ---------- Buttons ---------- */
export const BtnPrimary = ({ to, href, onClick, children, className = "", testid, ...rest }) => {
 const cls = `group inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-6 py-3.5 font-semibold text-white tracking-wide text-sm transition-all duration-300 hover:bg-racing-dark hover:shadow-[0_10px_30px_-8px_rgba(224,30,43,0.7)] active:scale-[0.98] ${className}`;
 if (to) return <Link to={to} className={cls} data-testid={testid} {...rest}>{children}</Link>;
 return <a href={href} onClick={onClick} className={cls} data-testid={testid} {...rest}>{children}</a>;
};

export const BtnGhost = ({ to, href, children, className = "", testid, ...rest }) => {
 const cls = `inline-flex items-center justify-center gap-2 rounded-md border border-white/25 bg-white/[0.03] px-6 py-3.5 font-semibold text-white tracking-wide text-sm transition-all duration-300 hover:border-primary hover:bg-white/[0.06] active:scale-[0.98] ${className}`;
 if (to) return <Link to={to} className={cls} data-testid={testid} {...rest}>{children}</Link>;
 return <a href={href} className={cls} data-testid={testid} {...rest}>{children}</a>;
};

export const CallBtn = ({ className = "", testid = "call-button" }) => (
 <BtnPrimary href={`tel:${PHONE_RAW}`} className={className} testid={testid}>
 <Phone size={16} /> {PHONE}
 </BtnPrimary>
);

export const ScheduleBtn = ({ className = "", testid = "schedule-button", label = "Schedule Now" }) => (
 <BtnPrimary to="/schedule-appointment" className={className} testid={testid}>
 <Calendar size={16} /> {label}
 </BtnPrimary>
);

/* ---------- Stars ---------- */
export const Stars = ({ value = 5, size = 16 }) => (
 <div className="flex items-center gap-0.5" role="img" aria-label={`${value} out of 5 stars`}>
 {[1, 2, 3, 4, 5].map((i) => (
 <Star key={i} size={size} aria-hidden="true" className={i <= Math.round(value) ? "fill-primary text-primary" : "text-white/20"} />
 ))}
 </div>
);

/* ---------- Trust badges ---------- */
export const TrustStrip = () => (
 <div className="border-y border-white/10 bg-steel/60" data-testid="trust-strip">
 <Container className="flex flex-wrap items-center justify-center gap-x-10 gap-y-5 py-8">
 {TRUST_BADGES.map((b) => (
 <img key={b.alt} src={b.src} alt={b.alt} title={b.alt} loading="lazy"
 className="h-16 w-auto object-contain sm:h-20" />
 ))}
 <div className="flex flex-col justify-center gap-1 pl-2 text-xs font-semibold tracking-wide text-muted-foreground">
 <span className="flex items-center gap-2"><Clock size={15} className="text-primary" /> Trusted Since 1995</span>
 <span className="flex items-center gap-2"><MapPin size={15} className="text-primary" /> BAR Licensed #265224</span>
 </div>
 </Container>
 </div>
);

/* ---------- Service card ---------- */
export const ServiceCard = ({ svc, index = 0 }) => (
 <motion.div
 initial={{ opacity: 0, y: 24 }}
 whileInView={{ opacity: 1, y: 0 }}
 viewport={{ once: true, margin: "-40px" }}
 transition={{ duration: 0.5, delay: (index % 3) * 0.08 }}
 >
 <Link
 to={`/services/${svc.slug}`}
 data-testid={`service-card-${svc.slug}`}
 className="group relative flex h-full flex-col overflow-hidden rounded-xl border border-white/10 bg-steel transition-all duration-300 hover:border-primary/60 hover:-translate-y-1"
 >
 <div className="relative h-44 overflow-hidden">
 <img src={svc.image} alt={`${svc.name} service at RPM Auto Repair in Riverside`} loading="lazy"
 className="h-full w-full object-cover opacity-70 transition-transform duration-500 group-hover:scale-110" />
 <div className="absolute inset-0 bg-gradient-to-t from-steel via-steel/30 to-transparent" />
 <span className="absolute left-4 top-4 rounded-full bg-primary/90 px-3 py-1 text-[11px] font-bold tracking-wider text-white">{svc.category}</span>
 </div>
 <div className="flex flex-1 flex-col p-5">
 <h3 className="font-display text-xl text-white group-hover:text-primary transition-colors">{svc.name}</h3>
 <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{svc.short}</p>
 <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold tracking-wide text-primary">
 Learn More <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
 </span>
 </div>
 </Link>
 </motion.div>
);

/* ---------- Breadcrumbs ---------- */
export const Breadcrumbs = ({ items }) => (
 <nav aria-label="Breadcrumb" className="mb-6">
 <ol className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
 {items.map((it, i) => (
 <li key={it.path} className="flex items-center gap-1.5">
 {i > 0 && <ChevronRight size={13} className="text-white/25" />}
 {i === items.length - 1 ? (
 <span className="text-white">{it.name}</span>
 ) : (
 <Link to={it.path} className="hover:text-primary transition-colors">{it.name}</Link>
 )}
 </li>
 ))}
 </ol>
 </nav>
);

/* ---------- Stat bar ---------- */
export const StatBar = () => {
 const stats = [
 { icon: Clock, num: "30+", label: "Years in Riverside" },
 { icon: Users, num: "50k+", label: "Vehicles Serviced" },
 { icon: Star, num: "4.4", label: "Average Rating" },
 { icon: ShieldCheck, num: "24/24k", label: "Mile Warranty" },
 ];
 return (
 <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-white/10 bg-white/10 lg:grid-cols-4">
 {stats.map(({ icon: Icon, num, label }) => (
 <div key={label} className="flex flex-col items-center bg-steel p-6 text-center">
 <Icon className="mb-3 text-primary" size={24} />
 <div className="font-display text-3xl font-600 text-white">{num}</div>
 <div className="mt-1 text-xs tracking-wide text-muted-foreground">{label}</div>
 </div>
 ))}
 </div>
 );
};

/* ---------- Map ---------- */
export const MapEmbed = ({ query = "2650 E Alessandro Blvd, Riverside, CA 92508", title = "RPM Auto Repair location map" }) => (
 <div className="overflow-hidden rounded-xl border border-white/10" data-testid="map-embed">
 <iframe
 title={title}
 className="h-full min-h-[300px] w-full grayscale-[0.3]"
 loading="lazy"
 referrerPolicy="no-referrer-when-downgrade"
 src={`https://maps.google.com/maps?q=${encodeURIComponent(query)}&z=12&output=embed`}
 />
 </div>
);

/* ---------- CTA banner ---------- */
export const CtaBanner = ({ title = "Ready for Honest, Expert Auto Repair?", text = "Get a free written estimate from Riverside's trusted ASE Master Certified shop. No pressure, no surprises, just quality work backed by a nationwide warranty." }) => (
 <Section className="relative overflow-hidden">
 <Container>
 <div className="grain relative overflow-hidden rounded-2xl border border-primary/30 bg-gradient-to-br from-[#1a1013] to-steel px-6 py-14 text-center sm:px-16">
 <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-primary/20 blur-3xl" />
 <div className="relative">
 <h2 className="mx-auto max-w-3xl font-display text-3xl sm:text-4xl lg:text-5xl font-600 leading-tight text-white">{title}</h2>
 <p className="mx-auto mt-5 max-w-2xl text-muted-foreground leading-relaxed">{text}</p>
 <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
 <ScheduleBtn testid="cta-schedule" label="Schedule Appointment" />
 <CallBtn testid="cta-call" />
 </div>
 </div>
 </div>
 </Container>
 </Section>
);

/* ---------- Inner page hero ---------- */
export const PageHero = ({ eyebrow, title, subtitle, image, crumbs }) => (
 <section className="relative overflow-hidden border-b border-white/10">
 {image && <img src={image} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-25" />}
 <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/85 to-ink" />
 <div className="absolute inset-0 grain" />
 <Container className="relative py-16 sm:py-20">
 {crumbs && <Breadcrumbs items={crumbs} />}
 {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
 <h1 className="mt-4 max-w-4xl font-display text-4xl font-700 leading-[1.05] text-white sm:text-5xl lg:text-6xl">{title}</h1>
 {subtitle && <p className="mt-5 max-w-2xl text-lg leading-relaxed text-white/75">{subtitle}</p>}
 </Container>
 </section>
);

export const Loader = () => (
 <div className="flex min-h-[50vh] items-center justify-center">
 <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/15 border-t-primary" />
 </div>
);

export { CheckCircle2, ShieldCheck, DollarSign, Wrench, Award, Phone, Calendar, MapPin, Clock, ChevronRight, ArrowRight, Star, Users };
